import React, { useEffect, useState } from "react";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Link } from "react-router-dom";
import { Trans } from "react-i18next";
import Select from "react-select";
import { get, post, del } from "helpers/api_helper";
import {
  CHANGE_ORDER_STATUS,
  ASSIGN_ORDER_DRIVER,
  CANCEL_ORDER,
  UNDO_CANCEL_ORDER,
  UNFREEZ_ORDER,
  FREE_ORDER,
  DRIVERS_SEARCH,
} from "helpers/api_endpoints";
import { FINAL_STATUSES } from "./orderFormat";

// Admin actions for one order. Every action is confirmed, then onDone(message)
// lets the page show the result and reload the order.
const OrderActions = ({ order, statuses, onDone, onError }) => {
  const [confirm, setConfirm] = useState(null); // {text, run, danger}
  const [busy, setBusy] = useState(false);
  const [driverModal, setDriverModal] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [driver, setDriver] = useState(null);

  const id = order.id;
  const statusId = order.currentStatusId;
  const locked = FINAL_STATUSES.includes(statusId);
  const nextStatus = statuses.find(s => s.value == statusId + 1);

  useEffect(() => {
    if (!driverModal || drivers.length > 0) return;
    post(DRIVERS_SEARCH)
      .then(response => {
        setDrivers((response.data || []).map(d => ({ value: d.id, label: `${d.id} - ${d.name}` })));
      })
      .catch(err => onError(err));
  }, [driverModal]);

  const run = (request, message) => {
    setBusy(true);
    request()
      .then(() => {
        setBusy(false);
        setConfirm(null);
        setDriverModal(false);
        onDone(message);
      })
      .catch(err => {
        setBusy(false);
        setConfirm(null);
        onError(err);
      });
  };

  const ask = (text, request, message, danger = false) =>
    setConfirm({ text, danger, run: () => run(request, message) });

  return (
    <div className="d-flex flex-wrap gap-2 order-actions">
      <Link to={`/orders/edit/${id}`} className="btn btn-sm btn-info">
        <i className="dripicons-pencil"></i> <Trans>Edit</Trans>
      </Link>

      {!locked && nextStatus && (
        <Button
          size="sm"
          color="primary"
          onClick={() =>
            ask(
              "Move order to the next status?",
              () => post(CHANGE_ORDER_STATUS, { orderId: id, statusId: statusId + 1 }),
              "Status changed successfully"
            )
          }
        >
          <i className="bx bx-right-arrow-alt"></i> <Trans>Move to</Trans>: <Trans>{nextStatus.label}</Trans>
        </Button>
      )}

      {!locked && (
        <Button size="sm" color="secondary" onClick={() => { setDriver(null); setDriverModal(true); }}>
          <i className="bx bx-user-check"></i> <Trans>{order.driverId ? "Change Driver" : "Assign Driver"}</Trans>
        </Button>
      )}

      {order.freezStatus == 1 && (
        <Button
          size="sm"
          color="info"
          onClick={() => ask("Unfreeze this order?", () => get(`${UNFREEZ_ORDER}/${id}`), "Order unfrozen")}
        >
          <i className="dripicons-snow"></i> <Trans>UnFreeze</Trans>
        </Button>
      )}

      {order.driverId && !locked && (
        <Button
          size="sm"
          color="warning"
          onClick={() => ask("Unassign the driver from this order?", () => post(FREE_ORDER, { orderId: id }), "Order unassigned")}
        >
          <i className="bx bx-user-x"></i> <Trans>Unassign Order</Trans>
        </Button>
      )}

      {statusId == 7 ? (
        <Button
          size="sm"
          color="success"
          onClick={() => ask("Restore this cancelled order?", () => del(UNDO_CANCEL_ORDER, { id }), "Restored SuccessFully")}
        >
          <i className="bx bx-undo"></i> <Trans>Remove Cancellation</Trans>
        </Button>
      ) : statusId != 6 ? (
        <Button
          size="sm"
          color="danger"
          onClick={() => ask("Cancel this order?", () => del(CANCEL_ORDER, { id }), "Cancelled SuccessFully", true)}
        >
          <i className="dripicons-trash"></i> <Trans>Cancel</Trans>
        </Button>
      ) : null}

      <Modal isOpen={!!confirm} toggle={() => !busy && setConfirm(null)} centered>
        <ModalBody className="px-4 py-5 text-center">
          <p className="text-muted font-size-16 mb-4"><Trans>{confirm?.text}</Trans></p>
          <div className="hstack gap-2 justify-content-center mb-0">
            <Button color={confirm?.danger ? "danger" : "primary"} disabled={busy} onClick={() => confirm.run()}>
              <Trans>{busy ? "Processing..." : "Yes"}</Trans>
            </Button>
            <Button color="secondary" disabled={busy} onClick={() => setConfirm(null)}>
              <Trans>No</Trans>
            </Button>
          </div>
        </ModalBody>
      </Modal>

      <Modal isOpen={driverModal} toggle={() => !busy && setDriverModal(false)} centered>
        <ModalHeader toggle={() => !busy && setDriverModal(false)}>
          <Trans>{order.driverId ? "Change Driver" : "Assign Driver"}</Trans>
        </ModalHeader>
        <ModalBody>
          <Select
            options={drivers}
            value={driver}
            onChange={setDriver}
            isLoading={drivers.length == 0}
            placeholder={<Trans>Select driver</Trans>}
          />
        </ModalBody>
        <ModalFooter>
          <Button
            color="primary"
            disabled={!driver || busy}
            onClick={() => run(() => post(ASSIGN_ORDER_DRIVER, { orderId: id, driverId: driver.value }), "Driver assigned successfully")}
          >
            <Trans>{busy ? "Processing..." : "Assign"}</Trans>
          </Button>
          <Button color="secondary" disabled={busy} onClick={() => setDriverModal(false)}>
            <Trans>Close</Trans>
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default OrderActions;
